import {
  ASPECT_RATIO_OPTIONS,
  COOKIE_STAFF_KEY,
  CURRENT_HOST,
  CURRENT_LANGUAGE,
  PLAY_HEAD_WRAPPER_PADDING,
  SMALL_DESKTOP_SIZE_BREAKPOINT,
  SMALL_MOBILE_SIZE_BREAKPOINT,
  PREMIUM_AI_MODELS_UPGRADE,
  textTypes
} from 'consts'
import { LOADING_FINAL_MANDATORY_VALUE } from 'services/generationProgress'
import { REF_LINKS } from 'consts/refLinks'
import { IAIImageModel } from 'store/types/aiImage'
import { IAIVideoModel } from 'types/aiVideo'
import { FileRegistry } from './file-registry/FileRegistry'
import { TMediaItemType } from 'types/media'

export const cutEmail = (email: string, maxLength: number = 29) => {
  if (!email) {
    return ''
  }

  if (email.length > maxLength) {
    const lastCount = email.indexOf('@')
    const endPart = email.slice(lastCount, email.length)
    const startPart = maxLength - endPart.length
    return email.substring(0, startPart + 1) + '...' + endPart
  }

  return email
}

export const getUrl = (relativePath: string = '') =>
  `${CURRENT_HOST}${relativePath}`

export const checkIsTargetEditable = (event: KeyboardEvent) => {
  // check if the event is on an input or editable HTML tag
  return (
    event.target instanceof HTMLInputElement ||
    event.target instanceof HTMLTextAreaElement ||
    (event.target instanceof HTMLElement && event.target.isContentEditable)
  )
}

export const isEmptyObject = <O>(obj: O) => {
  if (!obj) return true
  return Object.keys(obj).length === 0 && obj.constructor === Object
}

export const checkMetaKeys = ({
  configs,
  eventKeys
}: {
  eventKeys: { [key: string]: boolean }
  configs?: { [key: string]: boolean }
}) => {
  if (!configs || isEmptyObject(configs)) {
    return Object.values(eventKeys).every(key => key === false)
  }

  const { shiftKey, metaKey, ctrlKey } = eventKeys
  const metaOrCtrlKey = metaKey || ctrlKey

  if (configs.shiftKey && configs.metaKey) {
    return !!(shiftKey && metaOrCtrlKey)
  }
  if (configs.shiftKey) {
    return !!(shiftKey && !metaOrCtrlKey)
  }
  if (configs.metaKey) {
    return !!(metaOrCtrlKey && !shiftKey)
  }

  return false
}

const getLanguageQuery = (): string => {
  return CURRENT_LANGUAGE === 'en' ? '' : `/${CURRENT_LANGUAGE}`
}

export const goToWatchPage = (uid: string, quality: number): void => {
  const langQuery = getLanguageQuery()
  window.location.href = `${window.location.origin}${langQuery}/video-preview/${uid}?quality=${quality}`
}

export const goToProfileSubscriptionsPage = (): void => {
  const langQuery = getLanguageQuery()
  const url = `${CURRENT_HOST}${langQuery}/subscription`
  openBlankPage(url)
}

export const calculatePercentOf = (percent: number, number: number) => {
  return (percent / 100) * number
}

export const getPercentageFrom = (number: number, total: number) => {
  if (total === 0) return 0
  return (number / total) * 100
}

export const parseFloatAndFix = (fl: number | string, toFix: number = 3) => {
  return parseFloat((parseFloat(fl as string) || 0).toFixed(toFix))
}

export const capitalizeFirstLetter = (text?: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : ''

export const emptyFunction = <Args extends unknown[] = unknown[]>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ..._args: Args
): void => {
  return
}

export const scrollToElementById = (elementId: string): void => {
  const interval = setInterval(() => {
    const element = document.getElementById(elementId)
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
        block: 'nearest'
      })
      clearInterval(interval)
    }
  }, 100)

  setTimeout(() => clearInterval(interval), 3000)
}

export const divideTextFromVariable = (
  text: string
): { beforeSearchValuePart: string; afterSearchValuePart: string } => {
  const placeholder = '/%s/'
  const index = text.indexOf(placeholder)

  if (index === -1) {
    return { beforeSearchValuePart: text, afterSearchValuePart: '' }
  }

  return {
    beforeSearchValuePart: text.slice(0, index),
    afterSearchValuePart: text.slice(index + placeholder.length)
  }
}

export const playAudioAndCatchAbortError = (audio: HTMLAudioElement) => {
  audio.play().catch(error => {
    if (error.name === 'AbortError') {
      // Handle specific "play() was interrupted" error
      // eslint-disable-next-line no-console
      console.log("Play was interrupted, but it's okay.")
    }
  })
}

export const calculatePercentageOf = (part: number, whole: number): number =>
  (part / whole) * 100
export const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const sec = Math.floor(seconds % 60)
  const milliseconds = Math.floor((seconds - Math.floor(seconds)) * 10)

  return `${String(minutes).padStart(2, '0')}:${String(sec).padStart(2, '0')}:${milliseconds}`
}

export const getQueryParams = (
  params: Record<string, string | number | undefined>
): string => {
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined
  )

  const query = new URLSearchParams(
    entries.map(([key, value]) => [key, String(value)])
  )

  return query.toString()
}

export const downloadFileWithBlob = async (
  fetchResponse: Response,
  fileName: string
) => {
  const blob = await fetchResponse.blob()
  const fileRegistry = FileRegistry.getInstance()
  const blobUrl = fileRegistry.registerFile(blob)

  const a = document.createElement('a')
  a.href = blobUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)

  fileRegistry.deleteFile(blobUrl)
}

export const downloadMedia = async ({
  filePath,
  fileName = 'downloaded_file'
}: {
  filePath: string
  fileName?: string
}) => {
  try {
    const response = await fetch(filePath, {
      cache: 'no-store'
    })
    downloadFileWithBlob(response, fileName)
  } catch (error) {
    throw new Error(`Failed to download file: ${error}`)
  }
}

export const isBlobUrl = (url?: string | null): boolean => {
  return url?.startsWith('blob:') ?? false
}

export const getIsTouchDevice = (): boolean => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    !!(navigator as Navigator & { msMaxTouchPoints?: number }).msMaxTouchPoints
  )
}

export const scrollElementIntoView = (
  element: HTMLElement | Element | null,
  options: ScrollIntoViewOptions = { inline: 'nearest', block: 'nearest' }
) => {
  if (element) {
    element.scrollIntoView(options)
  }
}

export const getQueryStringForArrays = (
  filedName: string,
  values: string[]
) => {
  return values
    .map(value => `${filedName}[]=${encodeURIComponent(value)}`)
    .join('&')
}

export const generateRandomNumberId = () => Math.floor(Math.random() * 1000000)

export const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true

  if (
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    a === null ||
    b === null
  ) {
    return false
  }

  const keysA = Object.keys(a as Record<string, unknown>)
  const keysB = Object.keys(b as Record<string, unknown>)

  if (keysA.length !== keysB.length) return false

  return keysA.every(key =>
    isDeepEqual(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key]
    )
  )
}

export const getDeviceVersion = (
  size: number = SMALL_DESKTOP_SIZE_BREAKPOINT
): boolean => {
  const windowSize = {
    width: window.innerWidth,
    height: window.innerHeight
  }
  return windowSize.width <= size
}

export const createArrayWithKeys = (length: number) =>
  Array.from({ length }, (_, index) => {
    return {
      key: index
    }
  })

export const extractFileExtensionsFromMimeTypes = (
  mimeTypes: string[] | readonly string[]
): string => {
  return mimeTypes.map(type => type.split('/').pop()).join(', ')
}

export const getInitialDimensions = () => {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const aspectRatio = urlSearchParams.get('aspectRatio')

  if (!aspectRatio) {
    return ASPECT_RATIO_OPTIONS.landscape
  }

  return ASPECT_RATIO_OPTIONS[aspectRatio]
}

export const openBlankPage = (url: string) => {
  window.open(url, '_blank')
}

export const getPlayHeadWrapperPadding = () => {
  const isMobile = getDeviceVersion(SMALL_MOBILE_SIZE_BREAKPOINT)
  return isMobile ? PLAY_HEAD_WRAPPER_PADDING / 2 : PLAY_HEAD_WRAPPER_PADDING
}

export const flashSubSidebar = () => {
  const el = document.querySelector('[data-role="subsidebar-container"]')
  if (el instanceof HTMLElement) {
    el.setAttribute('data-flash', '1')
    setTimeout(() => el.removeAttribute('data-flash'), 1000)
  }
}

export const getCookie = (key: string): string | undefined => {
  const matches = document.cookie.match(
    new RegExp(
      '(?:^|; )' +
        key.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + // eslint-disable-line
        '=([^;]*)'
    )
  )
  return matches ? decodeURIComponent(matches[1]) : undefined
}

export const getIsUserFromStaff = (): boolean => !!getCookie(COOKIE_STAFF_KEY)

export const isLoadingFinalMandatoryValue = (
  progress: number | null
): boolean => {
  if (progress === null) return false
  return progress === LOADING_FINAL_MANDATORY_VALUE
}
export const isPremiumModel = (
  model: IAIImageModel | IAIVideoModel,
  userPlanId: number
): boolean => {
  if (!model || !model.tariffPlans || model.tariffPlans.length === 0) {
    return false
  }
  return !model.tariffPlans.includes(userPlanId)
}

export const handlePremiumModelClick = (
  model: IAIImageModel | IAIVideoModel | null | undefined,
  userPlanId: number
): boolean => {
  if (model && isPremiumModel(model, userPlanId)) {
    window.openUpgradePopup({
      type: PREMIUM_AI_MODELS_UPGRADE,
      openerRef: REF_LINKS.PREMIUM_AI_MODELS
    })
    return true
  }
  return false
}

export const removeUrlQueryParams = (url?: string | null) => {
  if (!url) return
  return url.split('?')[0]
}

export const isTextMediaItem = (type: TMediaItemType) =>
  textTypes.includes(type)

export const isValueUndefined = (value: unknown) => value === undefined
